import classNames from 'classnames';
import { type ComponentType, memo, type ReactNode, useCallback, useMemo, useRef } from 'react';
import { useDispatch } from 'react-redux';

import { type FiltersTrackingLocation } from '@air/analytics';
import { PlusIcon, Settings2Icon } from '@air/design-icons';
import { Button } from '@air/primitive-button';
import { useBreakpointsContext } from '@air/provider-media-query';

import { type ColorFilterCardProps } from '~/components/Filters/ColorFilter/ColorFilterCard';
import { type PublicCustomFieldFilterCardProps } from '~/components/Filters/CustomFieldFilter/PublicCustomFieldFilterCard';
import { CreatedDateFilterCard } from '~/components/Filters/DateFilter/CreatedDateFilterCard';
import { ModifiedDateFilterCard } from '~/components/Filters/DateFilter/ModifiedDateFilterCard';
import { TakenDateFilterCard } from '~/components/Filters/DateFilter/TakenDateFilterCard';
import { UploadedDateFilterCard } from '~/components/Filters/DateFilter/UploadedDateFilterCard';
import { type BaseFilterCardProps } from '~/components/Filters/FilterCard';
import { FiltersDropdownHeader } from '~/components/Filters/FiltersDropdown/FiltersDropdownHeader';
import { useFiltersDropdownController } from '~/components/Filters/FiltersDropdown/useFiltersDropdownController';
import { OtherFiltersCard } from '~/components/Filters/OtherFilters/OtherFiltersCard';
import { type SmartTagsFilterCardProps } from '~/components/Filters/SmartTagsFilter/SmartTagsFilterCard';
import { type TagsFilterCardProps } from '~/components/Filters/TagsFilter/TagsFilterCard';
import { TypeFilterCard } from '~/components/Filters/TypeFilter/TypeFilterCard';
import { type ItemTypeFilter } from '~/components/Filters/types';
import { type UploaderFilterCardProps } from '~/components/Filters/UploaderFilter/UploaderFilterCard';
import { FiltersMenu } from '~/components/FiltersMenu/FiltersMenu';
import {
  type CustomFieldFilterType,
  type FilterSelectType,
  type FilterType,
  type GetFiltersMenuOptionsParams,
} from '~/components/FiltersMenu/types';
import { useFiltersMenuOptions } from '~/components/FiltersMenu/useFiltersMenuOptions';
import { ADD_FILTER_BUTTON, APPLY_FILTERS_BUTTON, CLEAR_ALL_FILTERS_BUTTON } from '~/constants/testIDs';
import { addFilterAction } from '~/store/filters/actions';
import { type OtherFiltersType, type SelectedFilter } from '~/store/filters/types';

export type FiltersDropdownProps = {
  title: string;
  onApplyClick: () => void;
  availableTypes?: ItemTypeFilter[];
  otherFilters?: OtherFiltersType[];
  availableFilters: FilterType[];
  disabledFilters?: FilterType[];
  customFields?: CustomFieldFilterType[];
  OrientationFilterCard?: ComponentType<BaseFilterCardProps>;
  VideoFrameRateFilterCard?: ComponentType<BaseFilterCardProps>;
  VideoAspectRatioFilterCard?: ComponentType<BaseFilterCardProps>;
  AudioCodingFilterCard?: ComponentType<BaseFilterCardProps>;
  AudioSampleRateFilterCard?: ComponentType<BaseFilterCardProps>;
  ColorFilterCard?: ComponentType<Omit<ColorFilterCardProps, 'colors'>>;
  TagsFilterCard?: ComponentType<Omit<TagsFilterCardProps, 'TagsSelect'>>;
  SmartTagsFilterCard?: ComponentType<Omit<SmartTagsFilterCardProps, 'SmartTagsSelect'>>;
  CameraFilterCard?: ComponentType<BaseFilterCardProps>;
  SourceFilterCard?: ComponentType<BaseFilterCardProps>;
  CountryFilterCard?: ComponentType<BaseFilterCardProps>;
  CityFilterCard?: ComponentType<BaseFilterCardProps>;
  PeopleFilterCard?: ComponentType<BaseFilterCardProps>;
  StateFilterCard?: ComponentType<BaseFilterCardProps>;
  ExtensionFilterCard?: ComponentType<BaseFilterCardProps>;
  UploaderFilterCard?: ComponentType<Omit<UploaderFilterCardProps, 'uploaders'>>;
  ImportedKeywordsFilterCard?: ComponentType<BaseFilterCardProps>;
  CustomFieldFilterCard?: ComponentType<Omit<PublicCustomFieldFilterCardProps, 'CustomFieldValuesFilterSelect'>>;
  CopyrightFilterCard?: ComponentType<BaseFilterCardProps>;
  CreatorFilterCard?: ComponentType<BaseFilterCardProps>;
  LocationsFilterCard?: ComponentType<BaseFilterCardProps>;
  trackLocation: FiltersTrackingLocation;
  secondaryCTA?: ReactNode;
  isFilterChanged?: boolean;
};

export const FiltersDropdown = memo(
  ({
    title,
    customFields = [],
    onApplyClick,
    disabledFilters = [],
    ColorFilterCard,
    TagsFilterCard,
    SmartTagsFilterCard,
    CameraFilterCard,
    SourceFilterCard,
    ExtensionFilterCard,
    UploaderFilterCard,
    CustomFieldFilterCard,
    CopyrightFilterCard,
    CreatorFilterCard,
    availableTypes = ['boards', 'assets', 'files'],
    availableFilters,
    otherFilters,
    secondaryCTA,
    trackLocation,
    isFilterChanged,
    ImportedKeywordsFilterCard,
    CountryFilterCard,
    CityFilterCard,
    StateFilterCard,
    OrientationFilterCard,
    VideoFrameRateFilterCard,
    VideoAspectRatioFilterCard,
    AudioCodingFilterCard,
    AudioSampleRateFilterCard,
    PeopleFilterCard,
    LocationsFilterCard,
  }: FiltersDropdownProps) => {
    const dispatch = useDispatch();
    const filtersContainerRef = useRef<HTMLDivElement>(null);
    const { getFiltersMenuOptions } = useFiltersMenuOptions();
    const { isAboveMediumScreen } = useBreakpointsContext();

    const { applySelectedFilters, clearSelectedFilters, selectedFilters } = useFiltersDropdownController({
      availableFilters,
      doAfterApply: onApplyClick,
      customFields,
      trackLocation,
    });

    const onFilterSelect: FilterSelectType = useCallback(
      async (type, customField) => {
        dispatch(addFilterAction({ filter: { type, customField } }));
        filtersContainerRef.current?.scrollTo({ top: filtersContainerRef.current?.scrollHeight, behavior: 'smooth' });
      },
      [dispatch],
    );

    const getFiltersOptions = useCallback(
      (onSelect: GetFiltersMenuOptionsParams['onSelect']) =>
        getFiltersMenuOptions({
          availableFilters,
          selectedFilters,
          disabledFilters,
          onSelect,
          customFields,
        }),
      [availableFilters, customFields, disabledFilters, getFiltersMenuOptions, selectedFilters],
    );

    const addButtonFilters = useMemo(() => getFiltersOptions(onFilterSelect), [getFiltersOptions, onFilterSelect]);

    const cardProps = useMemo(
      (): Pick<BaseFilterCardProps, 'getFiltersOptions' | 'trackLocation'> => ({
        getFiltersOptions,
        trackLocation,
      }),
      [getFiltersOptions, trackLocation],
    );

    const renderFilterCard = useCallback(
      (filter: SelectedFilter) => {
        switch (filter.type) {
          case 'assetFileKind':
          case 'type':
            return <TypeFilterCard availableTypes={availableTypes} key={filter.type} {...cardProps} />;
          case 'color':
            return ColorFilterCard ? <ColorFilterCard key={filter.type} {...cardProps} /> : null;
          case 'tags':
            return TagsFilterCard ? <TagsFilterCard key={filter.type} {...cardProps} /> : null;
          case 'smartTags':
            return SmartTagsFilterCard ? <SmartTagsFilterCard key={filter.type} {...cardProps} /> : null;
          case 'other':
            return <OtherFiltersCard key={filter.type} otherFilters={otherFilters} {...cardProps} />;
          case 'dateCreated':
            return <CreatedDateFilterCard key={filter.type} {...cardProps} />;
          case 'dateModified':
            return <ModifiedDateFilterCard key={filter.type} {...cardProps} />;
          case 'dateUploaded':
            return <UploadedDateFilterCard key={filter.type} {...cardProps} />;
          case 'dateTaken':
            return <TakenDateFilterCard key={filter.type} {...cardProps} />;
          case 'source':
            return SourceFilterCard ? <SourceFilterCard key={filter.type} {...cardProps} /> : null;
          case 'extension':
            return ExtensionFilterCard ? <ExtensionFilterCard key={filter.type} {...cardProps} /> : null;
          case 'creator':
            return CreatorFilterCard ? <CreatorFilterCard key={filter.type} {...cardProps} /> : null;
          case 'uploader':
            return UploaderFilterCard ? <UploaderFilterCard key={filter.type} {...cardProps} /> : null;
          case 'copyright':
            return CopyrightFilterCard ? <CopyrightFilterCard key={filter.type} {...cardProps} /> : null;
          case 'importedKeywords':
            return ImportedKeywordsFilterCard ? <ImportedKeywordsFilterCard key={filter.type} {...cardProps} /> : null;
          case 'camera':
            return CameraFilterCard ? <CameraFilterCard key={filter.type} {...cardProps} /> : null;
          case 'customField':
            return filter.customField && CustomFieldFilterCard ? (
              <CustomFieldFilterCard key={filter.customField.id} customField={filter.customField} {...cardProps} />
            ) : null;
          case 'country':
            return CountryFilterCard ? <CountryFilterCard key={filter.type} {...cardProps} /> : null;
          case 'city':
            return CityFilterCard ? <CityFilterCard key={filter.type} {...cardProps} /> : null;
          case 'state':
            return StateFilterCard ? <StateFilterCard key={filter.type} {...cardProps} /> : null;
          case 'orientation':
            return OrientationFilterCard ? <OrientationFilterCard key={filter.type} {...cardProps} /> : null;
          case 'videoFrameRate':
            return VideoFrameRateFilterCard ? <VideoFrameRateFilterCard key={filter.type} {...cardProps} /> : null;
          case 'videoAspectRatio':
            return VideoAspectRatioFilterCard ? <VideoAspectRatioFilterCard key={filter.type} {...cardProps} /> : null;
          case 'audioCoding':
            return AudioCodingFilterCard ? <AudioCodingFilterCard key={filter.type} {...cardProps} /> : null;
          case 'audioSampleRate':
            return AudioSampleRateFilterCard ? <AudioSampleRateFilterCard key={filter.type} {...cardProps} /> : null;
          case 'people':
            return PeopleFilterCard ? <PeopleFilterCard key={filter.type} {...cardProps} /> : null;
          case 'locations':
            return LocationsFilterCard ? <LocationsFilterCard key={filter.type} {...cardProps} /> : null;
          default:
            return 'TBD';
        }
      },
      [
        availableTypes,
        cardProps,
        ColorFilterCard,
        TagsFilterCard,
        SmartTagsFilterCard,
        otherFilters,
        SourceFilterCard,
        ExtensionFilterCard,
        CreatorFilterCard,
        UploaderFilterCard,
        CopyrightFilterCard,
        ImportedKeywordsFilterCard,
        CameraFilterCard,
        CustomFieldFilterCard,
        CountryFilterCard,
        CityFilterCard,
        StateFilterCard,
        OrientationFilterCard,
        VideoFrameRateFilterCard,
        VideoAspectRatioFilterCard,
        AudioCodingFilterCard,
        AudioSampleRateFilterCard,
        PeopleFilterCard,
        LocationsFilterCard,
      ],
    );

    const hasAnyFilters = selectedFilters.length > 0;

    const trigger = useMemo(
      () => (
        <Button
          appearance="filled"
          color="gray"
          data-testid={ADD_FILTER_BUTTON}
          prefix={<PlusIcon size="sm" />}
          size="medium"
        >
          Add filter
        </Button>
      ),
      [],
    );

    const showFiltersContent = hasAnyFilters || !isAboveMediumScreen;

    return (
      <>
        <FiltersDropdownHeader title={title} />

        {showFiltersContent && (
          <div
            className={classNames(
              'scrollbar-thin scrollbar-track-gray-2 scrollbar-thumb-gray-7 flex flex-col overflow-auto',
              'gap-0 bg-gray-2 px-4 py-3 md:flex-1 md:bg-gray-1 md:p-0',
            )}
            ref={filtersContainerRef}
          >
            {!isAboveMediumScreen && !selectedFilters.length && (
              <div className="flex h-52 flex-col items-center justify-center rounded-8 border border-gray-3 bg-gray-1">
                <div className="flex flex-col items-center gap-4 text-center">
                  <Settings2Icon size={72} className="text-gray-7" />
                  <div className="text-gray-11">You have no filters applied.</div>
                </div>
              </div>
            )}
            {selectedFilters.map((filter) => renderFilterCard(filter))}
          </div>
        )}
        <div
          className={classNames(
            'flex justify-between py-3',
            'sticky bottom-0 bg-gray-2 px-4 md:-mt-px md:border-t md:border-t-gray-5 md:bg-gray-1',
          )}
        >
          <FiltersMenu options={addButtonFilters} trigger={trigger} />
          <div className="flex gap-2">
            {(!!secondaryCTA || !!selectedFilters.length) && (
              <Button
                appearance="outline"
                color="gray"
                data-testid={CLEAR_ALL_FILTERS_BUTTON}
                disabled={!selectedFilters.length}
                size="medium"
                onClick={clearSelectedFilters}
              >
                Clear
              </Button>
            )}
            <Button
              appearance="filled"
              color="blue"
              data-testid={APPLY_FILTERS_BUTTON}
              disabled={!isFilterChanged}
              size="medium"
              onClick={applySelectedFilters}
            >
              Apply
            </Button>
          </div>
        </div>
      </>
    );
  },
);

FiltersDropdown.displayName = 'FiltersDropdown';
